import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { DocumentCacheService } from '@nha/api/core/services/document-cache.service';
import { Message } from '@nha/types/document-loading-message.type';
import { Subscription, from, of, timer } from 'rxjs';
import { catchError, flatMap, map, switchMap, take, tap } from 'rxjs/operators';
import * as fromActions from '@nha/state/document-loading/document-loading.actions';
import { NHARootState } from '@nha/state/nha-root.state';

@Injectable()
export class DocumentLoadingEffects {
	private actions$ = inject(Actions);
	private store$ = inject<Store<NHARootState>>(Store);
	private documentCacheService = inject(DocumentCacheService);

	private _tabs: { [id: string]: Window } = {};
	private _retryActions: { [id: string]: Action } = {};
	private _delayedMessagesSub: Subscription;

	_errorMessage: Message = {
		text: 'Sometimes errors happen. This is one of those times.',
		subText: 'Let’s try again and see if we can generate your letters.',
		icon: 'error'
	};

	queueMessages$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromActions.DocumentLoadingActionTypes.QueueMessages),
				map((action: fromActions.QueueMessages) => action.payload),
				tap((payload) => {
					this.openOrRedirectTab(payload.id, payload.initialMessage);

					const messages$ = from(payload.delayMessages).pipe(
						// tslint:disable-next-line: deprecation
						flatMap(
							(delayedMessage) => timer(delayedMessage.timeDelay),
							(delayedMessage) => {
								this.sendDocMessage(payload.id, delayedMessage);
							}
						)
					);

					this._delayedMessagesSub = messages$.pipe(take(payload.delayMessages.length)).subscribe();
				})
			),
		{ dispatch: false }
	);

	requestSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromActions.DocumentLoadingActionTypes.RequestSuccess),
				map((action: fromActions.RequestSuccess) => action.payload),
				tap((payload) => {
					const pdf = new Blob([payload.stream], { type: 'application/pdf' });
					const fileURL = URL.createObjectURL(pdf);
					this._tabs[payload.id].location.href = fileURL;

					delete this._tabs[payload.id];
					delete this._retryActions[payload.id];
				})
			),
		{ dispatch: false }
	);

	typedRequestSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromActions.DocumentLoadingActionTypes.TypedRequestSuccess),
				map((action: fromActions.TypedRequestSuccess) => action.payload),
				tap((payload) => {
					const file = new Blob([payload.stream], { type: payload.reportOutputFormat.mimeType });
					const fileURL = URL.createObjectURL(file);
					this._tabs[payload.id].location.href = fileURL;
					if (payload.reportOutputFormat.fileExtension !== '.pdf') {
						setTimeout(() => {
							this._tabs[payload.id].close();
							delete this._tabs[payload.id];
							delete this._retryActions[payload.id];
						}, 1000);
					} else {
						delete this._tabs[payload.id];
						delete this._retryActions[payload.id];
					}
				})
			),
		{ dispatch: false }
	);

	requestByID$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromActions.DocumentLoadingActionTypes.RequestByID),
			map((action: fromActions.RequestByID) => action.payload),
			switchMap((payload) =>
				this.documentCacheService.getDocumentByID(payload.documentGuid).pipe(
					map((data) => {
						return new fromActions.RequestSuccess({ id: payload.id, stream: data });
					}),
					catchError((_) => {
						return of(
							new fromActions.RequestError({
								id: payload.id,
								message: this._errorMessage
							})
						);
					})
				)
			)
		)
	);

	requestError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromActions.DocumentLoadingActionTypes.RequestError),
				map((action: fromActions.RequestError) => action.payload),
				tap((payload) => {
					this._delayedMessagesSub.unsubscribe();
					setTimeout(() => {
						this.sendDocMessage(payload.id, payload.message);
						if (payload.message.icon === 'error-close') {
							delete this._tabs[payload.id];
							delete this._retryActions[payload.id];
						}
					}, 2000);
				})
			),
		{ dispatch: false }
	);

	setRetryAction$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromActions.DocumentLoadingActionTypes.SetRetryAction),
				map((action: fromActions.SetRetryAction) => action.payload),
				tap((payload) => {
					this._retryActions[payload.id] = payload.action;
				})
			),
		{ dispatch: false }
	);

	openOrRedirectTab(id: string, message: Message): void {
		const url: string = `document-loading.html?id=${id}&text=${message.text}&subText=${message.subText || ''}&icon=${message.icon}`;
		if (!this._tabs[id]) {
			this._tabs[id] = window.open(url);
		} else {
			this._tabs[id].location.href = url;
		}
	}

	sendDocMessage(id: string, message: Message): void {
		localStorage.setItem(id, JSON.stringify(message));
		localStorage.removeItem(id);
	}

	constructor() {
		window.addEventListener('storage', (event) => {
			if (event.key === 'retry-doc-gen' && this._retryActions[event.newValue]) {
				this.store$.dispatch(this._retryActions[event.newValue]);
			}
		});
	}
}
